import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  dashboardService,
  type PayerRole,
  type PeriodType,
} from "../../services/dashboard.service";
import { Select } from "../ui/Select";
import { DatePicker } from "../ui/DatePicker";
import { Tooltip } from "../ui/Tooltip";
import { InfoIcon } from "../icons/InfoIcon";

interface MetricRowProps {
  label: string;
  value: string | number;
  helpText?: string;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, value, helpText }) => {
  return (
    <div className="p-2 sm:p-3 border-b-2 border-border-gray last:border-b-0 sm:odd:border-r-2 sm:[&:nth-last-child(-n+2)]:border-b-0 min-[1248px]:odd:border-r-0 min-[1248px]:even:border-r-0 min-[1248px]:last:border-b-0 min-[1248px]:[&:nth-last-child(2)]:border-b-2">
      <div className="text-xs text-primary-blue uppercase mb-1 break-words flex items-center gap-1">
        <span>{label}</span>
        {helpText && (
          <Tooltip content={helpText}>
            <InfoIcon className="text-gray-500 hover:text-primary-blue transition-colors" />
          </Tooltip>
        )}
      </div>
      <div className="text-2xl font-medium break-words">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
};

// Helpers for date formatting and defaults
const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = now; // today
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

export const FinancialHealthWidget: React.FC = () => {
  const [periodType, setPeriodType] = useState<PeriodType>("current_month");
  const [payerRole, setPayerRole] = useState<PayerRole>("all");
  const [dateRange, setDateRange] = useState(() => getDefaultMonthRange());
  const [appliedDateRange, setAppliedDateRange] = useState(() => getDefaultMonthRange());

  // Compute start/end date from selector (backend does not support period param)
  const { startDate, endDate } = useMemo(() => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    if (periodType === "custom") {
      return { startDate: appliedDateRange.startDate, endDate: appliedDateRange.endDate };
    }

    if (periodType === "current_week") {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days
      const monday = new Date(now);
      monday.setDate(now.getDate() - daysToMonday);
      return { startDate: fmt(monday), endDate: fmt(now) };
    }

    if (periodType === "previous_week") {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const currentMonday = new Date(now);
      currentMonday.setDate(now.getDate() - daysToMonday);

      const prevMonday = new Date(currentMonday);
      prevMonday.setDate(currentMonday.getDate() - 7);

      const prevSunday = new Date(currentMonday);
      prevSunday.setDate(currentMonday.getDate() - 1);

      return { startDate: fmt(prevMonday), endDate: fmt(prevSunday) };
    }

    if (periodType === "previous_month") {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (periodType === "year_to_date") {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1); // January 1st of current year
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    if (periodType === "last_year") {
      const now = new Date();
      const start = new Date(now.getFullYear() - 1, 0, 1); // January 1st of last year
      const end = new Date(now.getFullYear() - 1, 11, 31); // December 31st of last year
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (periodType === "last_90_days") {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    // current_month default: 1st of current month to today
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { startDate: fmt(start), endDate: fmt(now) };
  }, [periodType, appliedDateRange]);

  // Fetch metrics
  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["financial-health-metrics", startDate, endDate, payerRole],
    queryFn: () => {
      const map: Record<PayerRole, string | undefined> = {
        primary: "PRIMARY",
        secondary: "SECONDARY",
        tertiary: "TERTIARY",
        patient: "PATIENT",
        primary_secondary: "PRIMARY_SECONDARY",
        all: undefined, // omit param for All
      };
      const payRoleParam = map[payerRole];
      return dashboardService.getFinancialHealthMetrics(startDate, endDate, payRoleParam);
    },
  });

  // Parse metrics and calculate derived values
  const metricsMap = useMemo(() => {
    const map: Record<string, string> = {};
    metrics?.forEach((metric) => {
      map[metric.metricName] = metric.value?.toString() || "0";
    });

    // Calculate Revenue Per Encounter: Payments / Encounters
    const payments = parseFloat(map["Payments"] || "0");
    const encounters = parseFloat(map["Encounters"] || "0");
    const revenuePerEncounter = encounters !== 0 ? payments / encounters : 0;
    map["Revenue Per Encounter"] = revenuePerEncounter.toFixed(2);

    return map;
  }, [metrics]);

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0";
    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatNumber = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0";
    return num.toLocaleString("en-US", { maximumFractionDigits: 0 });
  };

  return (
    <div className="bg-white border-2 border-border-gray flex flex-col">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Financial Health</h3>
        <span className="text-xs sm:text-sm">Overall Practice Performance</span>
      </div>

      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        <div className="flex-shrink-0 min-w-[80px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Payor Role</label>
          <Select value={payerRole} onChange={(e) => setPayerRole(e.target.value as PayerRole)}>
            <option value="all">All</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="primary_secondary">Primary+Secondary</option>
            <option value="tertiary">Tertiary</option>
            <option value="patient">Patient</option>
          </Select>
        </div>
        <div className="flex-shrink-0 min-w-[80px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as PeriodType)}
            className="w-full"
          >
            <option value="current_week">Current Week</option>
            <option value="previous_week">Previous Week</option>
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="year_to_date">Year to Date</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Period</option>
          </Select>
        </div>

        {periodType === "custom" && (
          <>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
              <DatePicker
                value={dateRange.startDate}
                maxDate={dateRange.endDate || undefined}
                onChange={(value) =>
                  setDateRange((prev: { startDate: string; endDate: string }) => ({
                    ...prev,
                    startDate: value,
                  }))
                }
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
              <DatePicker
                value={dateRange.endDate}
                minDate={dateRange.startDate || undefined}
                onChange={(value) =>
                  setDateRange((prev: { startDate: string; endDate: string }) => ({
                    ...prev,
                    endDate: value,
                  }))
                }
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const defaultRange = getDefaultMonthRange();
                setDateRange(defaultRange);
                setAppliedDateRange(defaultRange);
              }}
              className="h-10 px-3 bg-black text-white text-xs sm:text-sm hover:bg-gray-800 whitespace-nowrap flex-shrink-0"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => setAppliedDateRange(dateRange)}
              className="h-10 px-3 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue whitespace-nowrap flex-shrink-0"
            >
              Apply
            </button>
          </>
        )}
      </div>

      {/* Content */}
      <div>
        {isLoading && (
          <div className="text-center py-8 text-gray-500">Loading financial health data...</div>
        )}

        {error && (
          <div className="text-center py-8 text-red-600">
            Failed to load financial health data. Please try again.
          </div>
        )}

        {!isLoading && !error && (
          <div className="block sm:grid sm:grid-cols-2 min-[1248px]:block min-[1440px]:block">
            <MetricRow
              label="AVG DAYS IN A/R"
              value={formatNumber(metricsMap["Days in A/R"] || "0")}
              helpText="Average number of days claims remain outstanding in accounts receivable before payment is received. Lower values indicate faster collection."
            />
            <MetricRow
              label="TOTAL A/R"
              value={formatCurrency(metricsMap["Total A/R"] || "0")}
              helpText="Total amount of outstanding accounts receivable across all claims. This represents money owed but not yet collected."
            />
            <MetricRow
              label="EXCLUDED"
              value={formatNumber(metricsMap["Excluded"] || "0")}
              helpText="Number of claims excluded from financial calculations, typically due to special status or processing requirements."
            />
            <MetricRow
              label="ENCOUNTERS"
              value={formatNumber(metricsMap["Encounters"] || "0")}
              helpText="Total number of patient encounters or visits during the selected period."
            />
            <MetricRow
              label="CHARGES"
              value={formatCurrency(metricsMap["Charges"] || "0")}
              helpText="Total amount billed to insurance payers and patients for services rendered during the selected period."
            />
            <MetricRow
              label="PAYMENTS"
              value={formatCurrency(metricsMap["Payments"] || "0")}
              helpText="Total payments received from insurance payers and patients during the selected period."
            />
            <MetricRow
              label="REVENUE PER ENCOUNTER"
              value={formatCurrency(metricsMap["Revenue Per Encounter"] || "0")}
              helpText="Average revenue collected per encounter, calculated as total Payments divided by total Encounters. This metric shows the average reimbursement received per encounter."
            />
            <MetricRow
              label="UNBILLED"
              value={formatNumber(metricsMap["Unbilled"] || "0")}
              helpText="Number of patient encounters that have been completed but not yet billed to the payer."
            />
          </div>
        )}
      </div>
    </div>
  );
};
